import { forwardRef } from 'react'
import type { ISensemakerOverviewTeamMember } from '@brushfire/types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { formatOverviewDate, tenantInitials } from '../../lib/sensemaker-overview-format'
import {
  SENSEMAKER_OVERVIEW_TEAM_AVATAR_BG,
  SENSEMAKER_OVERVIEW_TEAM_AVATAR_GAP_PX,
  SENSEMAKER_OVERVIEW_TEAM_AVATAR_SIZE_PX,
  SENSEMAKER_OVERVIEW_TEAM_CARD_BORDER,
  SENSEMAKER_OVERVIEW_TEAM_CARD_GAP_PX,
  SENSEMAKER_OVERVIEW_TEAM_CARD_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_TEAM_CARD_PADDING_X_PX,
  SENSEMAKER_OVERVIEW_TEAM_CARD_PADDING_Y_PX,
  SENSEMAKER_OVERVIEW_TEAM_CARD_RADIUS_PX,
  SENSEMAKER_OVERVIEW_TEAM_JOINED_COLOR,
  SENSEMAKER_OVERVIEW_TEAM_JOINED_LINE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_TEAM_JOINED_SIZE_PX,
  SENSEMAKER_OVERVIEW_TEAM_NAME_COLOR,
  SENSEMAKER_OVERVIEW_TEAM_NAME_LETTER_SPACING_PX,
  SENSEMAKER_OVERVIEW_TEAM_NAME_LINE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_TEAM_NAME_SIZE_PX,
  SENSEMAKER_OVERVIEW_TEAM_NAME_TO_JOINED_GAP_PX,
  SENSEMAKER_OVERVIEW_TEAM_ROLE_BADGE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_TEAM_ROLE_STYLES,
  SENSEMAKER_OVERVIEW_SECTION_TITLE_TO_CONTENT_GAP_PX,
  sensemakerOverviewSectionTitleSx,
} from '../../lib/sensemaker-overview-layout'

interface Props {
  members: ISensemakerOverviewTeamMember[]
  highlight?: boolean
}

function getTeamRoleStyle(role: string) {
  const key = role.toUpperCase() as keyof typeof SENSEMAKER_OVERVIEW_TEAM_ROLE_STYLES
  return SENSEMAKER_OVERVIEW_TEAM_ROLE_STYLES[key] ?? SENSEMAKER_OVERVIEW_TEAM_ROLE_STYLES.VIEWER
}

function TeamMemberCard({ member }: Readonly<{ member: ISensemakerOverviewTeamMember }>) {
  const roleStyle = getTeamRoleStyle(member.role)

  return (
    <Box
      data-testid={`sensemaker-team-member-${member.memberId}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${SENSEMAKER_OVERVIEW_TEAM_AVATAR_GAP_PX}px`,
        height: `${SENSEMAKER_OVERVIEW_TEAM_CARD_HEIGHT_PX}px`,
        px: `${SENSEMAKER_OVERVIEW_TEAM_CARD_PADDING_X_PX}px`,
        py: `${SENSEMAKER_OVERVIEW_TEAM_CARD_PADDING_Y_PX}px`,
        border: SENSEMAKER_OVERVIEW_TEAM_CARD_BORDER,
        borderRadius: `${SENSEMAKER_OVERVIEW_TEAM_CARD_RADIUS_PX}px`,
        bgcolor: '#fff',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: `${SENSEMAKER_OVERVIEW_TEAM_AVATAR_SIZE_PX}px`,
          height: `${SENSEMAKER_OVERVIEW_TEAM_AVATAR_SIZE_PX}px`,
          borderRadius: '50%',
          bgcolor: SENSEMAKER_OVERVIEW_TEAM_AVATAR_BG,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '22.5px',
        }}
        aria-hidden
      >
        {tenantInitials(member.name)}
      </Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: `${SENSEMAKER_OVERVIEW_TEAM_NAME_SIZE_PX}px`,
              fontWeight: 600,
              lineHeight: `${SENSEMAKER_OVERVIEW_TEAM_NAME_LINE_HEIGHT_PX}px`,
              letterSpacing: `${SENSEMAKER_OVERVIEW_TEAM_NAME_LETTER_SPACING_PX}px`,
              color: SENSEMAKER_OVERVIEW_TEAM_NAME_COLOR,
            }}
          >
            {member.name}
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: `${SENSEMAKER_OVERVIEW_TEAM_ROLE_BADGE_HEIGHT_PX}px`,
              px: '11px',
              borderRadius: '999px',
              bgcolor: roleStyle.bg,
              border: `1px solid ${roleStyle.border}`,
              color: roleStyle.color,
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            {roleStyle.label}
          </Box>
        </Box>
        <Typography
          sx={{
            mt: `${SENSEMAKER_OVERVIEW_TEAM_NAME_TO_JOINED_GAP_PX}px`,
            fontFamily: 'Inter, sans-serif',
            fontSize: `${SENSEMAKER_OVERVIEW_TEAM_JOINED_SIZE_PX}px`,
            fontWeight: 400,
            lineHeight: `${SENSEMAKER_OVERVIEW_TEAM_JOINED_LINE_HEIGHT_PX}px`,
            color: SENSEMAKER_OVERVIEW_TEAM_JOINED_COLOR,
          }}
        >
          Joined&nbsp;{formatOverviewDate(member.joinDate)}
        </Typography>
      </Box>
    </Box>
  )
}

export const SensemakerOverviewTeamSection = forwardRef<HTMLDivElement, Props>(
  function SensemakerOverviewTeamSection({ members, highlight = false }, ref) {
  return (
    <Box
      ref={ref}
      id="sensemaker-overview-team-section"
      data-testid="sensemaker-overview-team-section"
      sx={{
        borderRadius: '10px',
        transition: 'box-shadow 0.3s',
        boxShadow: highlight ? '0 0 0 3px rgba(37, 99, 235, 0.35)' : 'none',
        scrollMarginTop: '109px',
      }}
    >
      <Typography
        component="h2"
        sx={{
          ...sensemakerOverviewSectionTitleSx,
          mb: `${SENSEMAKER_OVERVIEW_SECTION_TITLE_TO_CONTENT_GAP_PX}px`,
        }}
      >
        Team Members
      </Typography>
      {members.length === 0 ? (
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            color: SENSEMAKER_OVERVIEW_TEAM_JOINED_COLOR,
          }}
        >
          No team members yet. Individual accounts may not have a team roster.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${SENSEMAKER_OVERVIEW_TEAM_CARD_GAP_PX}px`,
          }}
        >
          {members.map((member) => (
            <TeamMemberCard key={member.memberId} member={member} />
          ))}
        </Box>
      )}
    </Box>
  )
  },
)
